import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { RootState } from '../../store/store';
import { isCompanyBrandingComplete } from '../../utils/branding';
import classNames from '../../utils/classNames';
import { ROUTES } from '../../utils/routes';
import { useSetupProgress } from '../../utils/setup';
import { hasActiveSubscription } from '../../utils/subscriptions';
import { showToast } from '../common/Notification';
import ParametrageContenuTab from './components/ParametrageContenuTab';
import ParametrageLancementTab from './components/ParametrageLancementTab';
import ParametragePlanningTab from './components/ParametragePlanningTab';
import ParametrageSetupTab from './components/ParametrageSetupTab';

const TABS = [
  { id: 'setup' as const, label: 'Paramétrage préalable', emoji: <><span role="img" aria-label="settings">&#x2699;</span></>, timeEstimate: null },
  { id: 'planning' as const, label: 'Planning', emoji: <><span role="img" aria-label="calendar">&#x1F4C5;</span></>, timeEstimate: '20 minutes en visio' },
  { id: 'contenu' as const, label: 'Contenu croissance', emoji: <><span role="img" aria-label="content">&#x1F4DD;</span></>, timeEstimate: '20 minutes en visio' },
  { id: 'lancement' as const, label: 'Lancement première newsletter', emoji: <><span role="img" aria-label="rocket">&#x1F680;</span></>, timeEstimate: '20 minutes après visio' },
];

type TabId = (typeof TABS)[number]['id'];

function ProgressRing({ percent, size = 64, strokeWidth = 5 }: { percent: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - ((percent / 100) * circumference);

  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="white"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-700 ease-out"
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="rotate-90 fill-white text-sm font-bold"
        style={{ transformOrigin: 'center' }}
      >
        {percent}%
      </text>
    </svg>
  );
}

function Parametrage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const company = useSelector((state: RootState) => state.auth.company);
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);

  // Inactive companies (no active subscription / not unlimited-free) have no
  // access to the setup parcours and are redirected to the parc vision dashboard.
  // Admins keep access so they can inspect inactive companies' setup.
  const shouldRedirectInactive = Boolean(company?.id) && !isAdmin && !hasActiveSubscription(company);

  useEffect(() => {
    if (shouldRedirectInactive) {
      showToast(
        'info',
        'Vous avez été redirigé vers votre parc de copropriétés. Le paramétrage détaillé sera accessible une fois que vous aurez un accès complet à Croissance, à l\'issue de votre démo.',
      );
      navigate(ROUTES.ANALYZING_MARKET_VISION, { replace: true });
    }
  }, [shouldRedirectInactive, navigate]);

  const isBrandingComplete = useMemo(() => isCompanyBrandingComplete(company), [company]);
  const { percent, completed, total } = useSetupProgress(company, isBrandingComplete);

  const isPlanningConfigured = Boolean(company?.toolsTimeline && Object.keys(company.toolsTimeline).length > 0);
  const hasFirstNewsletterSent = Boolean(company?.mandatoryNewsletterSentAt);

  const validTabIds = useMemo(() => TABS.map(t => t.id), []);

  // The active tab is derived purely from the URL so that switching tabs is a
  // router navigation. This lets `useBlocker` (in {@link ContentChoice}) guard
  // tab changes too whenever there are unsaved preferences.
  const activeTab: TabId = useMemo(() => {
    const requested = searchParams.get('tab');
    if (requested && (validTabIds as string[]).includes(requested)) {
      return requested as TabId;
    }
    if (percent < 100) {
      return 'setup';
    }
    if (!isPlanningConfigured) {
      return 'planning';
    }
    if (!hasFirstNewsletterSent) {
      return 'lancement';
    }
    return 'lancement';
  }, [percent, isPlanningConfigured, hasFirstNewsletterSent, searchParams, validTabIds]);

  const handleTabChange = (tabId: TabId) => {
    if (tabId === activeTab) { return; }
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  if (shouldRedirectInactive) {
    return null;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-rgpd-blue shadow-lg">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/[.07]" />

        <div className="relative flex flex-wrap items-center justify-between gap-6 px-8 py-6">
          <div className="min-w-0 flex-1">
            <h1 className="mb-1 text-[22px] font-bold text-white">
              Paramétrage de votre compte
            </h1>
            <p className="max-w-xl text-xs leading-relaxed text-blue-200">
              L&apos;ensemble du parcours vous permet d&apos;avoir tous les éléments pour mettre en place la stratégie
              globale. Les équipes de Déclarations Juridiques sont là pour vous accompagner à chaque étape si vous en
              avez besoin.
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-3.5">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-4">
                <ProgressRing percent={percent} />
                <div>
                  <div className="text-2xl font-bold text-white">{completed}/{total}</div>
                  <div className="text-[11px] text-blue-300">Étapes terminées</div>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.ANALYZING_MARKET_VISION)}
              className="rounded-lg border border-white/35 bg-white/15 px-4.5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-white/25"
            >
              Voir les chiffres clés &rarr;
            </button>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="mt-6">
        <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100 md:grid-cols-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={classNames(
                'flex min-h-20 cursor-pointer flex-col items-center justify-center gap-2 px-6 py-4 transition-all duration-300 hover:-translate-y-0.5',
                activeTab === tab.id
                  ? 'border-b-8 border-rgpd-green bg-white font-bold text-slate-800'
                  : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500',
              )}
            >
              <span className="text-xl">{tab.emoji}</span>
              <span className="text-center text-sm leading-relaxed">{tab.label}</span>
              {tab.timeEstimate && (
                <span className="text-[11px] font-normal text-gray-400">({tab.timeEstimate})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-8">
        {activeTab === 'setup' && <ParametrageSetupTab />}
        {activeTab === 'planning' && <ParametragePlanningTab />}
        {activeTab === 'contenu' && <ParametrageContenuTab />}
        {activeTab === 'lancement' && <ParametrageLancementTab />}
      </div>
    </div>
  );
}

export default Parametrage;
